import { getResultsExperiencePolicy } from "../../../packages/shared/src/screen-experience/useResultsExperiencePolicy";
import type {
	ResultsExperiencePolicyOutput,
	SharedLinkExperiencePolicyOutput,
} from "../../../packages/shared/src/types/screen-experience";
import type {
	TierId,
	TierSource,
	VisualLayer,
} from "../../../packages/shared/src/schemas/tier";

import {
	useDramaPlusConversionUxFlag,
	useGenerativeResultsFlag,
	useL3AccordionsFlag,
	useRewritesEnabledFlag,
	useSduiRenderingFlag,
	useSharedLinksFlag,
	useStreamingResultsFlag,
} from "./useFeatureFlags";
import type { DramaAnalysisResponse } from "../../../packages/shared/src/types/analysis";

interface UseResultsExperiencePolicyArgs {
	surface: "results" | "shared_link";
	accessToken: string | null;
	userId: string | null;
	isAnonymous: boolean;
	isRealUser?: boolean;
	authLoading: boolean;
	tier: TierId;
	tierLevel: 1 | 2 | 3;
	visualLayer: VisualLayer;
	source: TierSource;
	isPremium: boolean;
	isEntitlementsLoading: boolean;
	analysisCount: number | null;
	isTrialStateLoaded: boolean;
	analysis: DramaAnalysisResponse | null;
	hasRemoteConfig?: boolean;
	hasSharedLinkPayload?: boolean;
	errorCode?: string | null;
}

function deriveVisitState(
	isAnonymous: boolean,
	isTrialStateLoaded: boolean,
	analysisCount: number | null,
): "new" | "returning" | "unknown" {
	if (!isAnonymous) return "returning";
	if (!isTrialStateLoaded) return "unknown";
	return analysisCount === 0 ? "new" : "returning";
}

function deriveLifecycle(
	authLoading: boolean,
	isEntitlementsLoading: boolean,
	analysis: DramaAnalysisResponse | null,
	errorCode: string | null,
): "loading" | "ready" | "empty" | "error" {
	if (authLoading || isEntitlementsLoading) return "loading";
	if (errorCode) return "error";
	if (!analysis) return "empty";
	return "ready";
}

export function useResultsExperiencePolicy({
	surface,
	accessToken,
	userId,
	isAnonymous,
	isRealUser = !isAnonymous,
	authLoading,
	tier,
	tierLevel,
	visualLayer,
	source,
	isPremium,
	isEntitlementsLoading,
	analysisCount,
	isTrialStateLoaded,
	analysis,
	hasRemoteConfig = false,
	hasSharedLinkPayload = false,
	errorCode = null,
}: UseResultsExperiencePolicyArgs):
	| ResultsExperiencePolicyOutput
	| SharedLinkExperiencePolicyOutput {
	const sharedLinksEnabled = useSharedLinksFlag();
	const generativeResultsEnabled = useGenerativeResultsFlag();
	const sduiRenderingEnabled = useSduiRenderingFlag();
	const rewritesEnabled = useRewritesEnabledFlag();
	const streamingResultsEnabled = useStreamingResultsFlag();
	const conversionUxEnabled = useDramaPlusConversionUxFlag();
	useL3AccordionsFlag();

	const score = analysis?.result?.score ?? analysis?.score ?? null;
	const rewrites = analysis?.result?.rewrites ?? analysis?.rewrites ?? [];
	const hasPatterns = Boolean(analysis?.patterns?.length);
	const hasTransformations = Boolean(analysis?.transformations?.length);
	const hasDisplayableAnalysis =
		Boolean(analysis?.result) ||
		(typeof analysis?.score === "number" && typeof analysis?.category === "string");

	return getResultsExperiencePolicy({
		surface,
		auth: {
			identity: isAnonymous ? "anonymous" : "authenticated",
			isAnonymous,
			isRealUser,
			userId,
			hasAccessToken: Boolean(accessToken),
			isLoading: authLoading,
		},
		entitlements: {
			tier,
			tierLevel,
			visualLayer,
			source,
			isPremium,
			isLoading: isEntitlementsLoading,
		},
		trial: {
			analysisCount,
			visitState: deriveVisitState(
				isAnonymous,
				isTrialStateLoaded,
				analysisCount,
			),
			isLoaded: isTrialStateLoaded,
		},
		features: {
			isFlagsReady: true,
			treatment: "control",
			treatmentKey: null,
			signupVariant: null,
			sharedLinksEnabled,
			generativeResultsEnabled:
				generativeResultsEnabled && sduiRenderingEnabled,
			sduiRenderingEnabled,
			rewritesEnabled,
			crisisAssistanceEnabled: Boolean(analysis?.safety?.showResources),
			streamingResultsEnabled,
			trialPromptsEnabled: true,
			conversionUxEnabled,
		},
		data: {
			lifecycle: deriveLifecycle(
				authLoading,
				isEntitlementsLoading,
				analysis,
				errorCode,
			),
			hasAnalysis: Boolean(analysis),
			hasRemoteConfig,
			hasSharedLinkPayload,
			errorCode,
		},
		result: {
			hasDisplayableAnalysis,
			hasRewrites: rewrites.length > 0,
			hasPatterns,
			hasTransformations,
			isCircuitBreakerResponse: score === null && Boolean(analysis),
		},
	});
}
